import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'

import { useApi } from '../../../hooks'
import { formatToLocalTime } from '../../../utils/dates'
import toast from '../../toast'

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null
}

const getWorkflowBadgeStyle = (stage) => {
  if (!stage || !stage.color) return null
  const rgb = hexToRgb(stage.color)
  if (!rgb) return null
  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
    color: stage.color,
  }
}

const getWorkflowBadgeClass = (stage) => {
  if (!stage || !stage.name) return 'default'
  const name = stage.name.toLowerCase()
  if (name === 'added') return 'added'
  if (name === 'scheduled') return 'scheduled'
  if (name.includes('dropped') || name.includes('drop')) return 'dropped-off'
  if (name === 'complete' || name === 'completed') return 'complete'
  return 'default'
}

const VehicleHeader = ({ report, allowViewPricing, refetch }) => {
  const { adasReportService } = useApi()
  const [showModal, setShowModal] = useState(false)
  const [note, setNote] = useState('')

  const v = report.vehicle || {}
  const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ')
  const client = report.client || {}
  const insurance = (report.checkIn && report.checkIn.insurance) || {}
  const stockNumber = report.stockNumber || ''
  const portal = report.clientPortal || {}
  const notes = portal.notes || []
  const currentReady = portal.calibrationReady
  const totalPrice = report.totalPrice || 0
  const latestReadyNote = [...notes].reverse().find(n => n.status === 'yes')
  const submittedAt = portal.calibrationReadyDate || (latestReadyNote && latestReadyNote.createdAt)
  const submittedComment = latestReadyNote && latestReadyNote.text ? latestReadyNote.text.trim() : ''
  const workflowStage = report.workflowStage || null
  const workflowStyle = getWorkflowBadgeStyle(workflowStage)
  const workflowClass = getWorkflowBadgeClass(workflowStage)
  const reportHeaderLabel = report.number ? `ADAS Report #${report.number}` : 'ADAS Report'
  const createdDate = report.createdAt ? new Date(report.createdAt) : null
  const createdDateLabel = createdDate && !Number.isNaN(createdDate.getTime())
    ? createdDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—'
  const conversion = report.currentConversion || report.conversion
  const hasConvertedDoc = conversion && conversion.convertedTo && conversion.documentId

  let conversionPath = ''
  let conversionLabel = ''
  if (hasConvertedDoc) {
    if (conversion.convertedTo === 'invoice') {
      conversionPath = '/invoices/' + conversion.documentId
      conversionLabel = conversion.number ? `View Invoice #${conversion.number}` : 'View Invoice'
    } else if (conversion.convertedTo === 'estimate') {
      conversionPath = '/estimates/' + conversion.documentId
      conversionLabel = conversion.number ? `View Estimate #${conversion.number}` : 'View Estimate'
    } else if (conversion.convertedTo === 'work-order') {
      conversionPath = '/workorders/' + conversion.documentId
      conversionLabel = conversion.number ? `View Work Order #${conversion.number}` : 'View Work Order'
    }
  }

  const mutation = useMutation(
    () => adasReportService.updateReadiness({ id: report._id, calibrationReady: 'yes', note }),
    {
      retry: false,
      onSuccess: () => {
        toast.success({ title: 'Updated', body: 'Calibration readiness updated.' })
        setShowModal(false)
        setNote('')
        refetch()
      },
      onError: (err) => {
        toast.error({ title: 'Error', body: err.message || 'Failed to update readiness.' })
        setShowModal(false)
      },
    }
  )

  const viewMutation = useMutation(
    () => adasReportService.viewPdf({
      id: report._id,
      fileName: `adas_report_${report.number || report._id}.pdf`,
      showPricing: allowViewPricing && Number(totalPrice) > 0,
    }, true),
    { onError: err => toast.error({ title: 'View PDF errors', body: err.message || 'Could not open the ADAS report PDF.' }) }
  )

  const handleViewClick = () => {
    viewMutation.mutate()
  }

  const handleCalibrationClick = () => {
    setShowModal(true)
  }

  const clientStreet = client.street || ''
  const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ')

  return (
    <>
      <div className='adas-detail__vehicle-card'>
        <div className='adas-detail__vehicle-header'>
          <div className='adas-detail__vehicle-heading'>
            <div className='adas-detail__report-number'>{reportHeaderLabel}</div>
            <h2 className='adas-detail__vehicle-title'>{vehicle || 'Unknown Vehicle'}</h2>
          </div>
          <div className='adas-detail__action-buttons'>
            <button className='btn-outline-view' onClick={handleViewClick}>
              VIEW
            </button>
            <div className='adas-detail__calibration-block'>
              {conversionPath ? (
                <Link className='adas-detail__conversion-link' to={conversionPath}>
                  {conversionLabel}
                </Link>
              ) : currentReady === 'yes' ? (
                <button className='btn-calibration-ready-inactive' type='button' disabled>
                  READY FOR CALIBRATION
                </button>
              ) : (
                <button className='btn-primary-calibration' onClick={handleCalibrationClick}>
                  READY FOR CALIBRATION
                </button>
              )}
              {currentReady === 'yes' && (submittedAt || submittedComment) && (
                <div className='adas-detail__calibration-meta'>
                  {submittedAt && (
                    <div className='adas-detail__calibration-meta-line'>
                      Submitted on {formatToLocalTime(submittedAt)}
                    </div>
                  )}
                  {submittedComment && (
                    <div className='adas-detail__calibration-meta-line'>
                      Comment: {submittedComment}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='adas-detail__vehicle-content'>
          <div className='adas-detail__vehicle-details'>
            <div className='adas-detail__info-column'>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Color:</span>
                <span className='adas-detail__info-value'>{v.color || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Trim:</span>
                <span className='adas-detail__info-value'>{v.trim || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>VIN:</span>
                <span className='adas-detail__info-value'>{v.vin || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>STOCK/RO:</span>
                <span className='adas-detail__info-value'>{stockNumber || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Insurance Carrier:</span>
                <span className='adas-detail__info-value'>{insurance.name || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Insurance Pricing:</span>
                <span className='adas-detail__info-value adas-detail__muted-text'>
                  {insurance.pricing || 'Not Selected'}
                </span>
              </div>
            </div>

            <div className='adas-detail__divider' />
            <div className='adas-detail__client-info'>
              {client.name && <div>{client.name}</div>}
              {clientStreet && <div>{clientStreet}</div>}
              {clientCityStateZip && <div>{clientCityStateZip}</div>}
              {client.phone && <div>{client.phone}</div>}
              {client.email && <div>{client.email}</div>}
              <div className='adas-detail__date-created'>
                <span className='adas-detail__date-created-label'>Date Created:</span>
                <span className='adas-detail__date-created-value'>{createdDateLabel}</span>
              </div>
            </div>
          </div>

          <div className='adas-detail__right-column'>
            {allowViewPricing && (
              <div className='adas-detail__total'>
                <span className='adas-detail__total-label'>Total</span>
                <span className='adas-detail__total-amount'>
                  ${Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            <div className='adas-detail__workflow-status-row'>
              <span className='adas-detail__workflow-status-label'>Workflow Status:</span>
              {workflowStage && workflowStage.name ? (
                <span
                  className={`workflow-badge${!workflowStyle ? ` ${workflowClass}` : ''}`}
                  style={workflowStyle || undefined}
                >
                  {workflowStage.name}
                </span>
              ) : (
                <span className='adas-detail__workflow-status-empty'>—</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName='adas-confirm-modal'>
        <button className='adas-modal-close' onClick={() => setShowModal(false)} aria-label='Close'>&times;</button>
        <Modal.Body>
          <h3 className='adas-modal-title'>Submit as Ready for Calibration?</h3>
          <p className='adas-modal-copy'>This action can't be undone.</p>
          <label className='adas-modal-label'>Leave a note (optional)</label>
          <textarea
            className='adas-modal-textarea'
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder='Add any additional comments...'
          />
          <div className='adas-modal-footer'>
            <button
              className='adas-modal-submit'
              disabled={mutation.isLoading}
              onClick={() => mutation.mutate()}
            >
              {mutation.isLoading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default VehicleHeader
